/**
 * useOrder — 주문 상세 조회 훅 (인증 필수)
 * - AbortController로 컴포넌트 언마운트 시 요청 취소
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { orderApi, ApiClientError, type Order } from '@/lib/api';
import { MAX_RETRIES, fetchWithRetry, getApiErrorMessage } from './_shared';

interface UseOrderState {
  order: Order | null;
  loading: boolean;
  error: Error | null;
}

interface UseOrderReturn extends UseOrderState {
  refetch: () => Promise<void>;
}

/**
 * 주문 상세 조회 훅 (인증 필수)
 * - AbortController로 컴포넌트 언마운트 시 요청 취소
 */
export function useOrder(orderId: string | number | undefined): UseOrderReturn {
  const [state, setState] = useState<UseOrderState>({
    order: null,
    loading: true,
    error: null,
  });

  // 언마운트 후 상태 업데이트 방지
  const isMountedRef = useRef(true);
  // AbortController ref
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // 언마운트 시 진행 중인 요청 취소
      abortControllerRef.current?.abort();
    };
  }, []);

  const fetchOrder = useCallback(async () => {
    if (!orderId) {
      setState({ order: null, loading: false, error: null });
      return;
    }

    // 이전 요청 취소
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await fetchWithRetry(
        () => orderApi.getOrder(orderId),
        MAX_RETRIES,
        undefined,
        controller.signal
      );

      // 언마운트 체크
      if (!isMountedRef.current) return;

      setState({
        order: response.data,
        loading: false,
        error: null,
      });
    } catch (err) {
      // AbortError는 무시 (정상적인 취소)
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }

      // 언마운트 체크
      if (!isMountedRef.current) return;

      const message = getApiErrorMessage(err, '주문 정보를 불러오는데 실패했습니다');
      // #475 P1-1: ApiClientError 는 status 보존 — 권한/존재 분기 (401/403/404) 가 가능하도록 그대로 전달.
      // 그 외 에러는 메시지만 보존한 일반 Error 로 래핑.
      const wrappedError =
        err instanceof ApiClientError
          ? err
          : new Error(message);
      setState({
        order: null,
        loading: false,
        error: wrappedError,
      });
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  return {
    ...state,
    refetch: fetchOrder,
  };
}
